import React, { useEffect } from 'react';
import { Navbar, NavbarTabProps } from '@amzn/cuminuicomponents';
import { useRouter } from 'next/router';
import { useAppSelector, useAppDispatch } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import { fetchNotificationCount } from 'src/redux/slices/notifications';
import { ProfileStatus } from 'src/types/UserInfo';
import {
  NOTIFICATIONS,
  PREFERENCES,
  HOME_PAGE,
  THREAD_LISTING,
  GROUPS,
  pagePathMap,
} from 'src/constants/pages';
import { useMetrics, isGroupsVisibleForMpId, useTranslation } from 'src/utils';
import { ButtonTypes } from 'src/constants/metrics';
import FetchStatus from 'src/types/FetchStatus';
import MarketplaceSwitcher from './MarketplaceSwitcher';

const AppNavbar: React.FC = () => {
  const { t } = useTranslation();
  const { clickMetric } = useMetrics();
  const unreadCount = useAppSelector(({ notifications }: AppState) => notifications.unreadCount);
  const notificationCountStatus = useAppSelector(({ notifications }: AppState) => notifications
    .notificationCountStatus);
  const profileStatus = useAppSelector(({ userInfo }: AppState) => userInfo.profileStatus);
  const selectedMarketplaceId = useAppSelector(
    ({ userInfo }: AppState) => userInfo.selectedMarketplaceId,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (profileStatus === ProfileStatus.registered
      && notificationCountStatus !== FetchStatus.complete
    ) {
      dispatch(fetchNotificationCount());
    }
  }, [profileStatus]);

  const authenticatedTabs = [
    {
      label: t('pages_notifications'),
      routeTo: NOTIFICATIONS,
    },
    /**
     * @todo set the correct path when working on user profile
     * Sim - https://sim.amazon.com/issues/SPCE-2439
     */
    {
      label: t('pages_preferences'),
      routeTo: PREFERENCES,
    },
  ];

  const navbarTabs = [
    {
      label: t('pages_home'),
      routeTo: HOME_PAGE,
    },
    {
      label: t('pages_discussions'),
      routeTo: THREAD_LISTING,
    },
  ];

  if (isGroupsVisibleForMpId(selectedMarketplaceId)) {
    navbarTabs.push({
      label: t('pages_groups'),
      routeTo: GROUPS,
    });
  }

  if (profileStatus !== ProfileStatus.unauthenticated) {
    navbarTabs.push(...authenticatedTabs);
  }

  if (unreadCount) {
    const notificationTab = navbarTabs.find(
      (tab) => tab.routeTo === NOTIFICATIONS,
    ) as NavbarTabProps;
    if (notificationTab) {
      notificationTab.status = (
        <div
          data-testid="notification-unread-count"
          className="flex h-[1.3125rem] min-w-[1.3125rem] items-center justify-center rounded-full bg-kat-eastern px-[0.15rem] ltr:ml-1 rtl:mr-1"
        >
          <span className="text-kat-white">{unreadCount > 99 ? '99+' : unreadCount}</span>
        </div>
      );
    }
  }
  const { pathname, push } = useRouter();
  const requestedPath = `/${pathname.split('/')[1]}`;

  return (
    <nav data-testid="navbar" className="border-0 border-b border-solid border-kat-seattle bg-kat-azure">
      <div className="kat-row container mx-auto flex flex-row items-center justify-between text-xs">
        <div className="kat-col-md-10 flex flex-row space-x-2 rtl:space-x-reverse">
          <Navbar
            tabs={navbarTabs}
            onChange={(updatedPath) => {
              clickMetric({
                metricName: ButtonTypes.navbar,
                metricValue: pagePathMap[updatedPath],
              });
              push(updatedPath);
            }}
            selectedTab={requestedPath}
          />
        </div>
        <div data-testid="marketplace-picker" className="kat-col-md-2 ltr:pr-0 rtl:pl-0">
          <div className="flex justify-end">
            <MarketplaceSwitcher />
          </div>
        </div>
      </div>
    </nav>
  );
};
export default AppNavbar;
